import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import RippleButton from "../../components/common/RippleButton";
import ButtonForAll from "../../components/common/ButtonForAll";
import IconComponent from "../../components/common/IconComponent";
import { FaFilter } from "react-icons/fa";
import { Button } from "@progress/kendo-react-buttons";
import { RxCross1 } from "react-icons/rx";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useLocation } from "react-router-dom";
import {
  clearQuotationFilterDetails,
  setQuotationFilterDetails,
} from "./quotationSlice";
import { IQuotationFilterDetails } from "./quotationModel";

const QuotationFilter: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const filterContactID = location.state?.ContactID;
  const filterInquiryID = location.state?.InquiryID;
  const filterLeadID = location.state?.LeadID;
  const loading = useAppSelector((state) => state.lead.loading);
  const [expand, setExpand] = useState("");
  const QuotationFilterDetails = useAppSelector(
    (state) => state.quotation.QuotationFilterDetails
  );

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [QuotationFilterDetails]);

  useEffect(() => {
    const idMapping: Partial<
      Pick<
        IQuotationFilterDetails,
        "filterContactID" | "filterInquiryID" | "filterLeadID"
      >
    > = { filterContactID, filterLeadID, filterInquiryID };

    const updatedFilterDetails = Object.entries(idMapping).reduce(
      (acc, [key, value]) => {
        const typedKey = key as keyof IQuotationFilterDetails;

        return {
          ...acc,
          [typedKey]: QuotationFilterDetails?.[typedKey]
            ? `${QuotationFilterDetails[typedKey]}`
            : value
            ? `${value}`
            : "",
        };
      },
      {} as Partial<IQuotationFilterDetails>
    );

    if (
      Object.values(updatedFilterDetails).some((value) => value) &&
      JSON.stringify(updatedFilterDetails) !==
        JSON.stringify(QuotationFilterDetails)
    ) {
      dispatch(
        setQuotationFilterDetails({
          ...QuotationFilterDetails,
          ...updatedFilterDetails,
        })
      );
    }
  }, []);

  const handleSubmit = async (values: any) => {
    const insertPayload = {
      filterContactID:
        QuotationFilterDetails?.filterContactID || filterContactID || "",
      filterLeadID: QuotationFilterDetails?.filterLeadID || filterLeadID || "",
      filterInquiryID:
        QuotationFilterDetails?.filterInquiryID || filterInquiryID || "",

      filterName: values?.filterName ? values?.filterName : "",
      filterMobileNo: values?.filterMobileNo ? `${values?.filterMobileNo}` : "",
      filterEmailID: values?.filterEmailID ? values?.filterEmailID : "",
      filterPickUpPlace: values?.filterPickUpPlace
        ? values?.filterPickUpPlace
        : "",
      filterDropPlace: values?.filterDropPlace ? values?.filterDropPlace : "",
    };
    setExpand("");
    dispatch(setQuotationFilterDetails(insertPayload));
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <ExpansionPanel
        className="inquiry-filter-panel m-0"
        title={
          <div>
            <div>
              <IconComponent icon={FaFilter} /> Filter Results
            </div>
          </div>
        }
        subtitle={
          <div>
            <Tooltip anchorElement="target" position="top" parentTitle={true}>
              <Button
                title="Clear"
                themeColor={expand === "filter" ? "light" : "primary"}
                fillMode={"flat"}
                style={{ pointerEvents: "all" }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  dispatch(clearQuotationFilterDetails());
                }}
              >
                <IconComponent icon={RxCross1} />
              </Button>
            </Tooltip>
          </div>
        }
        expanded={expand === "filter"}
        tabIndex={1}
        onAction={(event: ExpansionPanelActionEvent) => {
          const nativeEvent = event.nativeEvent as KeyboardEvent;
          if (nativeEvent.type === "keydown" && nativeEvent.code === "Space") {
            nativeEvent.preventDefault();
            return;
          }

          if (event?.expanded) {
            setExpand("");
          } else {
            setExpand("filter");
          }
        }}
      >
        {expand === "filter" && (
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={QuotationFilterDetails}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement>
                <GridLayout
                  style={{ marginRight: 20, padding: 10 }}
                  gap={{ rows: 0, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem>
                    <Field
                      name="filterName"
                      label="Customer Name"
                      placeholder="Customer Name"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("filterName", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="filterMobileNo"
                      label="Mobile No"
                      type="number"
                      placeholder="i.e. 9999999999"
                      max={10}
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="filterEmailID"
                      label="Email ID"
                      placeholder="i.e. example@gmail.com"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="filterPickUpPlace"
                      label="PickUp Place"
                      placeholder="PickUp Place"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("filterPickUpPlace", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="filterDropPlace"
                      label="Drop Place"
                      placeholder="Drop Place"
                      maxLength="100"
                      component={FormTextField}
                      onKeyDown={(e: any) => {
                        if (e.key === " ") {
                          formRenderProps.onChange("filterDropPlace", {
                            value: `${e?.target?.value} `,
                          });
                        }
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                      marginTop: 20,
                    }}
                  >
                    <ButtonForAll
                      label={"Filter"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                    />

                    <RippleButton
                      type="button"
                      fillMode="outline"
                      themeColor="primary"
                      style={{ marginLeft: 4 }}
                      onClick={() => setExpand("")}
                    >
                      Cancel
                    </RippleButton>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            )}
          />
        )}
      </ExpansionPanel>
    </>
  );
};

export default QuotationFilter;
